import Accordion from './components/accordion/accordion';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';

import { BulletinsService } from 'src/services/bulletins.service';
import { ReactComponent as DocumentIcon } from 'src/assets/svg/bluelabel_document-pdf.svg';
import { getBrandByDealerNumber } from 'src/utils/helpers';
import { IUser } from 'src/utils/interfaces/user';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
// eslint-disable-next-line sort-imports
import {
    FlatDocument,
    GroupedDocument,
    TabConfig,
    useDocumentConfig,
} from './useDocumentConfig';
import React, { useEffect, useRef, useState } from 'react';

import './styles.scss';

const TableList = ({
    selected,
    tabConfig,
    setError,
}: {
    selected: string;
    tabConfig: TabConfig[];
    setError: Function;
}) => {
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const { i18n } = useTranslation();
    const language = i18n.language.includes('fr') ? 'fr' : 'en';

    const handlerClick = async (type: string, lang: string) => {
        const newTab = window.open('', '_blank');
        if (!newTab) return false;
        try {
            const service = new BulletinsService();
            const brand = dealerDataInfo
                ? getBrandByDealerNumber(
                      dealerDataInfo.dealer.brand,
                      dealerDataInfo.dealer.dealerNumber,
                  )
                : 'vw';
            const res = await service.getDocuments(
                brand,
                dealerDataInfo?.dealer.country || 'Canada',
                type,
                lang,
            );
            const contentType = String(res.headers?.['content-type'] || '');
            if (contentType.includes('application/json')) {
                const text =
                    res.data instanceof Blob
                        ? await res.data.text()
                        : JSON.stringify(res.data);
                const parsed = JSON.parse(text);
                newTab.location.assign(parsed.downloadUrl);
            } else {
                const pdfUrl = URL.createObjectURL(res.data);
                newTab.location.assign(pdfUrl);
            }
            setError(false);
            return true;
        } catch (e) {
            setError(true);
            newTab.document.body.innerHTML =
                '<h1>Failed to load PDF</h1><p>Please try again later.</p>';
            return false;
        }
    };

    const ele = tabConfig.find((t) => t.key === selected)?.documents ?? [];

    const renderItems = (
        items: (FlatDocument | GroupedDocument)[],
    ): React.ReactNode =>
        items.map((item, idx) => {
            if ('group' in item) {
                return (
                    <li key={idx}>
                        <Accordion
                            title={item.group}
                            content={renderItems(item.list)}
                        />
                    </li>
                );
            }
            return (
                <li key={idx}>
                    <DocumentIcon />
                    <a
                        href='#'
                        onClick={() =>
                            handlerClick(
                                item.type,
                                item.lang ? item.lang : language,
                            )
                        }
                    >
                        {item.title}
                    </a>
                </li>
            );
        });

    return (
        <div
            className='documents-container'
            style={{
                minHeight: ele.length <= Number('4') ? '550px' : undefined,
            }}
        >
            <ul>{renderItems(ele)}</ul>
        </div>
    );
};
const DocumentsComponent = () => {
    const { documents } = useLocalization();
    const tabConfig = useDocumentConfig();
    const [showNotify, setShowNotify] = useState(false);
    const [errorNotification, setErrorNotification] = useState('');
    const [isError, setIsError] = useState(false);
    const [tab, setTab] = useState('GENERAL');
    const navRef = useRef<HTMLDivElement>(null);

    const scrollTabs = (direction: 'left' | 'right') => {
        if (!navRef.current) return;

        const keys = tabConfig.map((t) => t.key);
        const currentIndex = keys.findIndex((k) => k === tab);

        const newIndex =
            direction === 'left'
                ? Math.max(Number('0'), currentIndex - Number('1'))
                : Math.min(
                      keys.length - Number('1'),
                      currentIndex + Number('1'),
                  );

        setTab(keys[newIndex]);

        const newTabElement = navRef.current.querySelectorAll('li')[newIndex];
        if (newTabElement) {
            (newTabElement as HTMLElement).scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
            });
        }
    };

    useEffect(() => {
        if (isError) {
            setErrorNotification(documents.DOCUMENTS_VIEW_ERROR);
            setShowNotify(true);
        } else {
            setErrorNotification('');
            setShowNotify(false);
        }
    }, [isError]);

    return (
        <>
            <div className='documents-wrapper'>
                {showNotify && (
                    <Notification
                        content={errorNotification}
                        type={
                            isError
                                ? NotificationType.ERROR
                                : NotificationType.SUCCESS
                        }
                        visibility={showNotify}
                        setVisibility={setIsError}
                    />
                )}
                <div className='page-title'>
                    <h5>{documents.DOCUMENTS_VIEW_TITLE}</h5>
                </div>
                <div className='content-container'>
                    <div className='c-tabs'>
                        <div className='c-tabs__nav-container'>
                            <nav aria-label='Tabs Navigation'>
                                <button
                                    className='c-tabs__arrow c-tabs__arrow--left'
                                    onClick={() => scrollTabs('left')}
                                >
                                    &lt;
                                </button>
                                <div className='c-tabs__nav' ref={navRef}>
                                    {tabConfig.map((t, index) => {
                                        return (
                                            <div
                                                key={`${index}-${t.key}`}
                                                className='c-tabs__nav-item'
                                            >
                                                <button
                                                    className={`c-tabs__nav-link ${
                                                        tab == t.key
                                                            ? 'is-active'
                                                            : ''
                                                    }`}
                                                    onClick={() => {
                                                        setTab(t.key);
                                                    }}
                                                    type='button'
                                                >
                                                    <a
                                                        className='c-link'
                                                        data-testid='link-item-faqs'
                                                    >
                                                        <span className='c-link__body'>
                                                            {t.label}
                                                        </span>
                                                    </a>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                                <button
                                    className='c-tabs__arrow c-tabs__arrow--right'
                                    onClick={() => scrollTabs('right')}
                                >
                                    &gt;
                                </button>
                            </nav>
                        </div>
                    </div>
                    <>
                        <TableList
                            selected={tab}
                            tabConfig={tabConfig}
                            setError={setIsError}
                        />
                    </>
                </div>
                <div className='privacy-footer-container '>
                    <FooterPrivacy />
                </div>
            </div>
        </>
    );
};

export default DocumentsComponent;
